import { StrictMode, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  CheckCircle2,
  Clipboard,
  Copy,
  Download,
  Droplet,
  Eye,
  Grid3X3,
  Palette,
  SlidersHorizontal,
  Sparkles,
  SunMedium,
  XCircle
} from "lucide-react";
import "./styles.css";

type RGB = { r: number; g: number; b: number };

const palette = ["#0f172a", "#334155", "#475569", "#64748b", "#94a3b8", "#cbd5e1", "#e2e8f0", "#f8fafc", "#ffffff", "#dc2626", "#059669", "#2563eb"];
const backgrounds = ["#ffffff", "#f8fafc", "#f1f5f9", "#e2e8f0", "#cbd5e1", "#94a3b8", "#475569", "#0f172a", "#000000"];

function clamp(value: number, min = 0, max = 255) {
  return Math.max(min, Math.min(max, value));
}

function normalizeHex(value: string) {
  const clean = value.trim().replace("#", "");
  if (/^[0-9a-fA-F]{3}$/.test(clean)) {
    return `#${clean.split("").map((char) => char + char).join("").toLowerCase()}`;
  }
  if (/^[0-9a-fA-F]{6}$/.test(clean)) return `#${clean.toLowerCase()}`;
  return "#000000";
}

function hexToRgb(value: string): RGB {
  const hex = normalizeHex(value).slice(1);
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16)
  };
}

function rgbToHex({ r, g, b }: RGB) {
  return `#${[r, g, b].map((value) => clamp(Math.round(value)).toString(16).padStart(2, "0")).join("")}`;
}

function linear(channel: number) {
  const value = channel / 255;
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function luminance(color: string) {
  const { r, g, b } = hexToRgb(color);
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

function wcagRatio(fg: string, bg: string) {
  const lighter = Math.max(luminance(fg), luminance(bg));
  const darker = Math.min(luminance(fg), luminance(bg));
  return (lighter + 0.05) / (darker + 0.05);
}

function apcaEstimate(fg: string, bg: string) {
  const text = luminance(fg);
  const back = luminance(bg);
  if (back > text) return (back ** 0.56 - text ** 0.57) * 114;
  return (back ** 0.65 - text ** 0.62) * 114;
}

function mixWith(color: string, target: RGB, amount: number) {
  const base = hexToRgb(color);
  return rgbToHex({
    r: base.r + (target.r - base.r) * amount,
    g: base.g + (target.g - base.g) * amount,
    b: base.b + (target.b - base.b) * amount
  });
}

function scoreLabel(ratio: number) {
  if (ratio >= 7) return "AAA";
  if (ratio >= 4.5) return "AA";
  if (ratio >= 3) return "AA large";
  return "Fail";
}

function apcaLabel(lc: number) {
  const abs = Math.abs(lc);
  if (abs >= 75) return "Strong";
  if (abs >= 60) return "Body";
  if (abs >= 45) return "Large";
  return "Fail";
}

function App() {
  const [foreground, setForeground] = useState("#0f172a");
  const [background, setBackground] = useState("#ffffff");
  const [mode, setMode] = useState<"wcag" | "apca">("apca");
  const [nudge, setNudge] = useState(0);
  const [copied, setCopied] = useState("");

  const adjustedForeground = useMemo(() => mixWith(foreground, nudge >= 0 ? { r: 0, g: 0, b: 0 } : { r: 255, g: 255, b: 255 }, Math.abs(nudge) / 100), [foreground, nudge]);
  const ratio = wcagRatio(adjustedForeground, background);
  const lc = apcaEstimate(adjustedForeground, background);
  const cssTokens = `:root {\n  --color-fg: ${adjustedForeground};\n  --color-bg: ${background};\n  --contrast-wcag: ${ratio.toFixed(2)};\n  --contrast-apca-lc: ${lc.toFixed(0)};\n}`;

  async function copyText(text: string, label: string) {
    await navigator.clipboard.writeText(text);
    setCopied(label);
    window.setTimeout(() => setCopied(""), 1300);
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <a data-google-vignette="false" className="brand" href="/">
          <span><Grid3X3 size={23} /></span>
          <div>
            <h1>ContrastGrid APCA</h1>
            <small>WCAG 2.2 and APCA-style color contrast workbench</small>
          </div>
        </a>
        <nav aria-label="Primary">
          <a data-google-vignette="false" href="#checker">Checker</a>
          <a data-google-vignette="false" href="#grid">Palette Grid</a>
          <a data-google-vignette="false" href="#tokens">Tokens</a>
        </nav>
        <a data-google-vignette="false" href="/privacy.html" className="utility-link">Privacy</a>
      </header>

      <section className="workspace">
        <aside className="checker-panel" id="checker">
          <PanelTitle number="1" title="Select pair" icon={<Droplet size={18} />} />
          <ColorField label="Foreground" value={foreground} setValue={setForeground} />
          <ColorField label="Background" value={background} setValue={setBackground} />
          <button className="swap-button" type="button" onClick={() => { setForeground(background); setBackground(foreground); }}>
            <Copy size={17} /> Swap pair
          </button>

          <PanelTitle number="2" title="Contrast mode" icon={<Eye size={18} />} />
          <div className="segment">
            <button className={mode === "wcag" ? "active" : ""} type="button" onClick={() => setMode("wcag")}>WCAG 2.2</button>
            <button className={mode === "apca" ? "active" : ""} type="button" onClick={() => setMode("apca")}>APCA Lc</button>
          </div>

          <section className="result-card">
            <span>{mode === "wcag" ? "WCAG ratio" : "APCA-style Lc"}</span>
            <strong>{mode === "wcag" ? ratio.toFixed(2) : Math.round(lc)}</strong>
            <em className={mode === "wcag" ? scoreLabel(ratio).toLowerCase().replace(" ", "-") : apcaLabel(lc).toLowerCase()}>
              {mode === "wcag" ? scoreLabel(ratio) : apcaLabel(lc)}
            </em>
            <ul>
              <li><span>AA normal</span><strong>{ratio >= 4.5 ? "Pass" : "Fail"}</strong></li>
              <li><span>AAA normal</span><strong>{ratio >= 7 ? "Pass" : "Fail"}</strong></li>
              <li><span>APCA body target</span><strong>{Math.abs(lc) >= 60 ? "Pass" : "Review"}</strong></li>
            </ul>
          </section>

          <section className="preview-card" style={{ color: adjustedForeground, background }}>
            <strong>The five boxing wizards jump quickly.</strong>
            <p>Preview body text, links, labels, and captions before shipping UI.</p>
          </section>
        </aside>

        <section className="grid-panel" id="grid">
          <div className="grid-heading">
            <PanelTitle number="3" title="Palette grid" icon={<Palette size={18} />} />
            <button type="button" onClick={() => copyText(cssTokens, "css")}><Clipboard size={17} /> {copied === "css" ? "Copied" : "Copy CSS"}</button>
          </div>
          <div className="matrix">
            <div className="corner">Text / Surface</div>
            {backgrounds.map((bg) => <ColorHeader key={bg} value={bg} />)}
            {palette.map((fg) => (
              <PaletteRow key={fg} foreground={fg} backgrounds={backgrounds} />
            ))}
          </div>
          <div className="legend">
            <span><i className="pass" /> AA or better</span>
            <span><i className="warn" /> Large text only</span>
            <span><i className="fail" /> Fails</span>
          </div>
        </section>

        <aside className="adjust-panel">
          <PanelTitle number="4" title="Adjust foreground" icon={<SlidersHorizontal size={18} />} />
          <div className="slider-field">
            <label>Lightness nudge <strong>{nudge}%</strong></label>
            <input aria-label="Adjust foreground lightness" type="range" min="-70" max="70" value={nudge} onChange={(event) => setNudge(Number(event.target.value))} />
          </div>
          <div className="swatch-stack">
            <div><span>Original</span><i style={{ background: foreground }} /></div>
            <div><span>Adjusted</span><i style={{ background: adjustedForeground }} /></div>
            <div><span>Background</span><i style={{ background }} /></div>
          </div>

          <section className="token-card" id="tokens">
            <PanelTitle number="5" title="Export tokens" icon={<Download size={18} />} />
            <pre>{cssTokens}</pre>
            <button type="button" onClick={() => copyText(cssTokens, "tokens")}>
              <Clipboard size={17} /> {copied === "tokens" ? "Copied" : "Copy tokens"}
            </button>
          </section>

          <section className="note-card">
            <SunMedium size={18} />
            <p>APCA values are presented as a design-review estimate. Verify critical experiences against WCAG 2.2 and real user testing.</p>
          </section>
        </aside>
      </section>

      <section className="preset-panel">
        <div>
          <Sparkles size={18} />
          <h2>Accessible palette presets</h2>
          <p>Use the matrix to pick accessible foreground/background combinations for dashboards, forms, docs, and dark mode surfaces.</p>
        </div>
        <div className="preset-row">
          {palette.slice(0, 10).map((color) => <button key={color} style={{ background: color }} title={color} onClick={() => setForeground(color)} />)}
        </div>
      </section>

      <footer>
        <span>Independent contrast checker. Guidance only; verify critical UI against WCAG 2.2 and real users.</span>
        <nav>
          <a data-google-vignette="false" href="/privacy.html">Privacy</a>
          <a data-google-vignette="false" href="/about.html">About</a>
        </nav>
      </footer>
    </main>
  );
}

function PanelTitle({ number, title, icon }: { number: string; title: string; icon: React.ReactNode }) {
  return (
    <div className="panel-title">
      <span>{number}</span>
      {icon}
      <strong>{title}</strong>
    </div>
  );
}

function ColorField({ label, value, setValue }: { label: string; value: string; setValue: (value: string) => void }) {
  return (
    <label className="color-field">
      <span>{label}</span>
      <div>
        <input type="color" value={normalizeHex(value)} onChange={(event) => setValue(event.target.value)} />
        <input value={value} onChange={(event) => setValue(event.target.value)} spellCheck={false} />
      </div>
    </label>
  );
}

function ColorHeader({ value }: { value: string }) {
  return (
    <div className="color-head">
      <i style={{ background: value }} />
      <span>{value}</span>
    </div>
  );
}

function PaletteRow({ foreground, backgrounds }: { foreground: string; backgrounds: string[] }) {
  return (
    <>
      <div className="row-head">
        <i style={{ background: foreground }} />
        <span>{foreground}</span>
      </div>
      {backgrounds.map((bg) => {
        const ratio = wcagRatio(foreground, bg);
        const label = scoreLabel(ratio);
        const status = ratio >= 4.5 ? "pass" : ratio >= 3 ? "warn" : "fail";
        return (
          <div key={bg} className={`matrix-cell ${status}`} style={{ color: foreground, background: bg }}>
            <strong>{ratio.toFixed(1)}</strong>
            <span>{label}</span>
            {status === "fail" ? <XCircle size={13} /> : <CheckCircle2 size={13} />}
          </div>
        );
      })}
    </>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
